import * as XLSX from 'xlsx';

interface EnrollmentWithApproval {
  id: string;
  short_id?: string; // 6자리 축소판 ID (엑셀용)
  user_id: string;
  class_id: string;
  status: 'ACTIVE' | 'CANCELLED';
  certificate_approved: boolean;
  certificate_approved_at: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    phone?: string;
  };
}

interface ExportData {
  함축ID: string;
  이름: string;
  휴대폰뒷자리: string;
  신청일: string;
  승인여부: '승인' | '비승인';
}

/**
 * Format date for Excel export
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Extract last 4 digits from phone number
 */
function getPhoneLast4Digits(phone?: string): string {
  if (!phone) return '';
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  // Return last 4 digits
  return digits.slice(-4);
}

/**
 * Export enrollments to Excel file
 */
export function exportToExcel(
  enrollments: EnrollmentWithApproval[],
  classTitle: string
): void {
  const data: ExportData[] = enrollments.map((e) => ({
    함축ID: e.short_id || e.id.substring(0, 6), // short_id 우선, 없으면 UUID 앞 6자
    이름: e.user?.name || '이름 없음',
    휴대폰뒷자리: getPhoneLast4Digits(e.user?.phone),
    신청일: formatDate(e.created_at),
    승인여부: e.certificate_approved ? '승인' : '비승인',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '수강신청자');

  // Set column widths
  worksheet['!cols'] = [
    { wch: 10 }, // 함축ID
    { wch: 15 }, // 이름
    { wch: 12 }, // 휴대폰뒷자리
    { wch: 12 }, // 신청일
    { wch: 10 }, // 승인여부
  ];

  const fileName = `${classTitle}_인증서승인목록.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

/**
 * Import approvals from Excel file
 */
export async function importFromExcel(
  file: File
): Promise<{ enrollmentId: string; approved: boolean; phoneLast4: string }[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<ExportData>(worksheet);

        const approvals = json.map((row) => ({
          enrollmentId: row.함축ID,
          approved: row.승인여부 === '승인',
          phoneLast4: row.휴대폰뒷자리 || '',
        }));

        resolve(approvals);
      } catch (error) {
        reject(new Error('엑셀 파일 처리 중 오류가 발생했습니다.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('파일을 읽는 중 오류가 발생했습니다.'));
    };

    reader.readAsArrayBuffer(file);
  });
}
